import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/contexts/LanguageContext'
import { getSortedCountries, getCountryLabel } from '@/constants/countries'

interface CountryComboboxProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  id?: string
}

/**
 * Searchable combobox for selecting an ISO 3166 country code
 */
export function CountryCombobox({
  value,
  onChange,
  disabled = false,
  id,
}: CountryComboboxProps) {
  const { t, language } = useLanguage()
  const [open, setOpen] = useState(false)

  const availableCountries = getSortedCountries(language)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {value
            ? getCountryLabel(value, language)
            : t.dialogs.newOrganization.billingCountryPlaceholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command shouldFilter={true}>
          <CommandInput placeholder={t.dialogs.newOrganization.billingCountrySearch} />
          <CommandList className="max-h-[330px]">
            <CommandEmpty>{t.dialogs.newOrganization.billingCountryNoResults}</CommandEmpty>
            <CommandGroup>
              {availableCountries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={country.translations[language]}
                  onSelect={() => {
                    onChange(country.code)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === country.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {country.translations[language]}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
